/* assets/recent-products-plus.js  (CDIONIS-77 r2 + CDIONIS-74, revision 5)
 *
 * Enhanced "Take Another Look" (recently viewed) renderer for Blockshop
 * 12.2.1. Used by sections/recent-products.liquid. The stock
 * recent-products-element in theme.js remains defined but unused.
 *
 * r2: theme featured-collection pattern. One display-type setting
 *     (slideshow | grid) at every breakpoint; slideshow reuses the
 *     theme's carousel-element (desktop side arrows, mobile header pager,
 *     swipe everywhere).
 * r3: Buy Again label (CDIONIS-74). decorateCard adds a BUY AGAIN label
 *     to cards whose product id is in the signed-in purchased set
 *     (rendered by snippets/recent-products-buy-again.liquid). It matches
 *     the theme's product--label markup and styling (color/shape/text
 *     color come from the theme's global product label settings, passed
 *     via data attributes). Product id comes from the tracked history
 *     (item.id); no dependency on the BazaarVoice markup.
 * r4 (not deployed): literal implementation of Kim's 2026-07-16 priority
 *     list (Sold Out beats Buy Again, Buy Again removes New only).
 * r5: full no-stack per design review with Baruch (2026-07-16). Kim's
 *     intent is one label per card, so on a purchased, in-stock card
 *     BUY AGAIN now owns the corner: New, Sale, and featured-tag labels
 *     all yield. Nothing is lost: sale pricing still shows in the price
 *     row (strikethrough compare-at, NOW / percent pills), and featured
 *     tags still show on every card that does not get Buy Again. Sold
 *     Out still wins outright (no Buy Again, card untouched). Detection
 *     and removal use the theme's own data-label-type attributes from
 *     the display_labels / featured_label captures in
 *     snippets/product-card.liquid, so there is no text or locale
 *     coupling, and labels without that attribute are never removed.
 *
 * Data layer: versioned MRU history, theme-name independent. Writer lives
 * in snippets/recent-products-tracker.liquid. Keep STORAGE_KEY and
 * HISTORY_LIMIT in sync with that snippet.
 */
(() => {
  'use strict';

  if (customElements.get('recent-products-plus')) return;

  const STORAGE_KEY = 'dionis:recent-products:v2';
  const HISTORY_LIMIT = 12;
  const BATCH_SIZE = 4;
  const CARD_SELECTOR = '.product-card--root';

  const validItem = (item) =>
    !!item && typeof item.handle === 'string' && item.handle.length > 0;

  const readHistory = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.version === 2 && Array.isArray(parsed.items)) {
          return parsed.items.filter(validItem);
        }
      }
    } catch (error) {
      return [];
    }
    return migrateLegacy();
  };

  const writeHistory = (items) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ version: 2, items: items.slice(0, HISTORY_LIMIT) })
      );
    } catch (error) {
      /* Storage unavailable. Non-fatal. */
    }
  };

  /* Stock theme writer stores up to 4 handles, oldest first, under a
   * theme-name-derived key. Migrate once, newest first. */
  const migrateLegacy = () => {
    try {
      const legacyKey = `${Shopify.theme.name}:recent_products`;
      const legacy = JSON.parse(localStorage.getItem(legacyKey) || '[]');
      if (!Array.isArray(legacy)) return [];
      const items = legacy
        .filter((handle) => typeof handle === 'string' && handle.length > 0)
        .reverse()
        .map((handle) => ({ id: null, handle: handle, viewedAt: 0 }));
      if (items.length) writeHistory(items);
      return items;
    } catch (error) {
      return [];
    }
  };

  class RecentProductsPlus extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._state = 'idle';
      this._abort = new AbortController();

      this.host = this.querySelector('[data-recent-products-host]');
      this.spinner = this.querySelector('.recent-products--spinner');
      this.headerNav = this.querySelector('.recent-products--header-nav');
      this.carouselTemplate = this.querySelector('[data-carousel-template]');
      this.navTemplate = this.querySelector('[data-nav-template]');

      this.sectionId = this.dataset.sectionId;
      this.displayType = this.dataset.displayType || 'grid';
      this.desktopColumns = this.clampInt(this.dataset.desktopColumns, 3, 4, 4);
      this.mobileColumns = this.clampInt(this.dataset.mobileColumns, 1, 2, 2);
      this.maxProducts = this.clampInt(this.dataset.maxProducts, 4, HISTORY_LIMIT, 4);

      this.buyAgain = this.dataset.buyAgain === 'true' ? this.readPurchased() : null;

      if (!this.host) return;

      if (Shopify.designMode) {
        document.addEventListener(
          'shopify:section:select',
          (event) => {
            if (event.target.contains(this) && this._state === 'idle') this.load();
          },
          { signal: this._abort.signal }
        );
        this.load();
      } else {
        this.observeViewport();
      }
    }

    disconnectedCallback() {
      this._abort?.abort();
      this._io?.disconnect();
      this._initialized = false;
    }

    clampInt(value, min, max, fallback) {
      const parsed = parseInt(value, 10);
      if (Number.isNaN(parsed)) return fallback;
      return Math.min(max, Math.max(min, parsed));
    }

    /* Reads the inline purchased-set JSON emitted (signed-in only) by
     * snippets/recent-products-buy-again.liquid, plus the theme's global
     * product-label styling passed on this element. Returns null when the
     * purchased set is absent (logged out, or setting off), which
     * disables the label. */
    readPurchased() {
      try {
        const node = this.querySelector('[data-recent-products-purchased]');
        if (!node) return null;
        const data = JSON.parse(node.textContent);
        if (!data || !Array.isArray(data.ids) || !data.ids.length) return null;
        return {
          ids: new Set(data.ids.map((id) => String(id))),
          label: (data.label || 'BUY AGAIN').toString(),
          color: this.dataset.buyAgainLabelColor || '',
          shape: this.dataset.buyAgainLabelShape || '',
          textColor: this.dataset.buyAgainTextColor || 'body'
        };
      } catch (error) {
        return null;
      }
    }

    observeViewport() {
      this._io = new IntersectionObserver(
        ([entry], observer) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: `${window.innerHeight}px 0px` }
      );
      this._io.observe(this);
    }

    async load() {
      if (this._state !== 'idle') return;
      this._state = 'loading';
      const signal = this._abort.signal;

      const items = this.excludeCurrent(readHistory());
      if (!items.length) return this.commitEmpty();

      const misses = [];
      let results = [];
      try {
        results = await this.fetchCards(items, signal, misses);
      } catch (error) {
        if (error.name === 'AbortError') return;
        results = [];
      }
      if (signal.aborted || !this.isConnected) return;

      if (misses.length) this.pruneMisses(misses);

      if (!results.length) return this.commitEmpty();
      this.commit(results);
    }

    excludeCurrent(items) {
      const currentId = this.dataset.currentProductId;
      const currentHandle = (this.dataset.currentProductHandle || '').toLowerCase();
      return items.filter((item) => {
        if (currentId && String(item.id) === String(currentId)) return false;
        if (currentHandle && item.handle.toLowerCase() === currentHandle) return false;
        return true;
      });
    }

    async fetchCards(items, signal, misses) {
      const results = [];
      for (
        let offset = 0;
        offset < items.length && results.length < this.maxProducts;
        offset += BATCH_SIZE
      ) {
        const batch = items.slice(offset, offset + BATCH_SIZE);
        const settled = await Promise.all(
          batch.map((item) => this.fetchCard(item, signal))
        );
        for (const result of settled) {
          if (result.status === 'ok' && results.length < this.maxProducts) {
            results.push(result);
          } else if (result.status === 'gone') {
            misses.push(result.handle);
          }
        }
      }
      return results;
    }

    async fetchCard(item, signal) {
      const url = new URL(
        `${Shopify.routes.root}products/${encodeURIComponent(item.handle)}`,
        window.location.origin
      );
      url.searchParams.set('view', 'ajax-item');

      let response;
      try {
        response = await fetch(url, { signal });
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        return { status: 'transient', handle: item.handle };
      }

      if (response.status === 404 || response.status === 410) {
        return { status: 'gone', handle: item.handle };
      }
      if (!response.ok) return { status: 'transient', handle: item.handle };

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = doc.querySelector(CARD_SELECTOR);
      if (!card) return { status: 'transient', handle: item.handle };

      card.setAttribute('data-transition-item', this.sectionId);
      this.decorateCard(card, item);
      return { status: 'ok', card: card, item: item, handle: item.handle };
    }

    /* CDIONIS-74 Buy Again. Runs synchronously while the card is
     * disconnected, before any layout initializes (no CLS). Adds a BUY
     * AGAIN label when the feature is on, the customer is signed in, and
     * this card's product was purchased, subject to label priority.
     *
     * Priority (Kim 2026-07-16, finalized with Baruch same day):
     *   1. Sold Out: if the theme rendered its out-of-stock label
     *      (data-label-type="out-of-stock"), the product is unavailable.
     *      Buy Again is skipped and the card is left fully native. That
     *      branch is not behind a theme setting (only Sale and New are),
     *      so the label is a reliable render-time availability signal.
     *      Cards are fetched fresh on every render, so a restocked
     *      product picks Buy Again back up automatically.
     *   2. Buy Again stands alone: when added, every other label in the
     *      corner is removed (New, Sale, featured tag). No information is
     *      lost: sale pricing still shows in the price row (strikethrough
     *      compare-at, NOW / percent pills), and featured tags still show
     *      on every card that does not get Buy Again.
     *
     * Two safety guarantees:
     *   - A card that does not get Buy Again is never modified.
     *   - Removal only targets labels carrying the theme's
     *     data-label-type attribute, so unexpected markup degrades to
     *     visible stacking, never to a wrongly hidden label (a Sold Out
     *     badge can never be eaten by this code).
     *
     * Styling matches the theme's product--label exactly: color, shape,
     * and text color come from the theme's global product label settings
     * (settings.product--label-color / --label-shape / --label-white-text)
     * passed on this element, so the label looks identical to a native
     * NEW badge with zero new CSS.
     *
     * Product id comes from the tracked history entry (item.id). Legacy
     * migrated entries predate id tracking (id: null) and are skipped
     * until re-viewed; no dependency on the BazaarVoice markup. */
    decorateCard(card, item) {
      if (!this.buyAgain) return;
      if (item.id == null) return;
      if (!this.buyAgain.ids.has(String(item.id))) return;

      const linkWrapper = card.querySelector('.product-card--image-wrapper');
      if (!linkWrapper) return;

      let labels = linkWrapper.querySelector('.product--labels');

      /* Priority 1: Sold Out beats Buy Again. Card left untouched. */
      if (labels && labels.querySelector('[data-label-type="out-of-stock"]')) {
        return;
      }

      if (!labels) {
        labels = document.createElement('div');
        labels.className = 'product--labels';
        labels.setAttribute('data-item', 'overline');
        labels.setAttribute('data-text-color', this.buyAgain.textColor);
        linkWrapper.insertBefore(labels, linkWrapper.firstChild);
      }

      /* Idempotent: never add a second Buy Again label. */
      if (labels.querySelector('[data-label-type="buy-again"]')) return;

      /* Priority 2: Buy Again stands alone. Guarded removal: only labels
       * that declare a data-label-type are removed. */
      labels
        .querySelectorAll(
          '.product--label[data-label-type]:not([data-label-type="buy-again"])'
        )
        .forEach((node) => node.remove());

      const label = document.createElement('div');
      label.className = 'product--label';
      label.setAttribute('data-label-type', 'buy-again');
      if (this.buyAgain.color) label.setAttribute('data-background-color', this.buyAgain.color);
      if (this.buyAgain.shape) label.setAttribute('data-label-shape', this.buyAgain.shape);
      label.textContent = this.buyAgain.label;
      labels.appendChild(label);
    }

    pruneMisses(goneHandles) {
      const gone = new Set(goneHandles.map((handle) => handle.toLowerCase()));
      writeHistory(readHistory().filter((item) => !gone.has(item.handle.toLowerCase())));
    }

    commit(results) {
      const cards = results.map((result) => result.card);
      const useCarousel =
        this.displayType === 'slideshow' && cards.length > 0 && !!this.carouselTemplate;

      const layout = useCarousel ? this.buildCarousel(cards) : this.buildGrid(cards);
      const nav = useCarousel ? this.buildNav() : null;

      requestAnimationFrame(() => {
        if (!this.isConnected) return;

        if (this.headerNav) {
          this.headerNav
            .querySelectorAll('.carousel-nav-arrow')
            .forEach((old) => old.remove());
          if (nav) this.headerNav.append(nav);
        }

        this.host.replaceChildren(layout);
        this.host.setAttribute('aria-hidden', 'false');
        this.spinner?.setAttribute('aria-hidden', 'true');
        this.setAttribute('aria-busy', 'false');
        this._state = 'ready';

        window.trigger?.(`theme:transition:reload:${this.sectionId}`);

        if (useCarousel) this.settleCarouselHeight(layout);

        this.dispatchEvent(
          new CustomEvent('recent-products:rendered', {
            bubbles: true,
            detail: {
              sectionId: this.sectionId,
              cards: cards,
              products: results.map((result) => result.item)
            }
          })
        );
      });
    }

    /* Height reconciliation for the injected carousel.
     *
     * The theme's Carousel measures full card height (--block-height) once
     * in getHeights(), triggered by load() on an IntersectionObserver +
     * rAF. Because our cards are injected asynchronously and their price
     * row and BazaarVoice inline rating compose over the next few frames,
     * that first measurement can capture a card that is still short and
     * lock --slide-height too small, clipping the price. Native
     * featured-collection avoids this only because its cards are fully
     * server-rendered before the carousel ever measures.
     *
     * Fix, theme-native: once our images have decoded (and after a couple
     * of frames for BV/price to settle), call the theme's own load() to
     * force a clean full remeasure using the theme's height math. No
     * custom height logic; the site's MutationObserver rescue continues to
     * catch any later BV shift on top of this. Guarded and best-effort. */
    settleCarouselHeight(carousel) {
      const runLoad = () => {
        if (!carousel || !carousel.isConnected) return;
        try {
          if (typeof carousel.load === 'function') {
            carousel.load();
          } else {
            carousel.getHeights?.();
            carousel.setSliderHeight?.();
            carousel.updateArrowPos?.();
          }
        } catch (error) {
          /* Non-fatal: leave the theme's own rescue to reconcile. */
        }
      };

      const images = [...carousel.querySelectorAll('img')];
      const decoded = images.map((img) =>
        img.complete ? Promise.resolve() : (img.decode ? img.decode().catch(() => {}) : Promise.resolve())
      );

      Promise.all(decoded).then(() => {
        /* Two rAFs: let layout flush, then let BV/price paint settle. */
        requestAnimationFrame(() => requestAnimationFrame(runLoad));
      });

      /* Safety net if images never resolve (blocked/slow): remeasure late. */
      setTimeout(runLoad, 1200);
    }

    commitEmpty() {
      this._state = 'empty';
      this.spinner?.setAttribute('aria-hidden', 'true');
      this.setAttribute('aria-busy', 'false');

      if (Shopify.designMode) {
        this.host.setAttribute('aria-hidden', 'false');
        this.host.innerHTML =
          '<p data-item="paragraph" style="text-align: center; opacity: 0.65; margin: 0;">' +
          'Recently viewed products appear here after a shopper views products. ' +
          'The section hides automatically when there is nothing to show.</p>';
      } else {
        this.setAttribute('aria-hidden', 'true');
      }
    }

    buildGrid(cards) {
      const grid = document.createElement('div');
      grid.className = 'recent-products--grid';
      grid.setAttribute('data-container', 'panel');
      grid.setAttribute('data-columns', String(this.desktopColumns));
      grid.setAttribute('data-fixed-columns', 'true');
      grid.setAttribute('data-mobile-columns', String(this.mobileColumns));
      grid.append(...cards);
      return grid;
    }

    buildCarousel(cards) {
      const shell = this.carouselTemplate.content.firstElementChild.cloneNode(true);
      const track = shell.querySelector('.carousel--container');

      const totalSlides = Math.ceil(cards.length / this.desktopColumns);
      const totalSlidesMobile = Math.ceil(cards.length / this.mobileColumns);

      shell.style.setProperty('--blocks-per-slide', String(this.desktopColumns));
      shell.style.setProperty('--blocks-per-slide-mobile', String(this.mobileColumns));
      shell.style.setProperty('--total-slides', String(totalSlides));
      shell.style.setProperty('--total-slides-mobile', String(totalSlidesMobile));
      shell.style.setProperty('--total-spaces', String(this.desktopColumns * totalSlides));
      shell.style.setProperty(
        '--total-spaces-mobile',
        String(this.mobileColumns * totalSlidesMobile)
      );

      if (totalSlides < 2) {
        shell
          .querySelectorAll('.carousel--prev, .carousel--next')
          .forEach((arrow) => arrow.remove());
      }

      cards.forEach((card, index) => {
        const block = document.createElement('div');
        block.className = 'carousel--block';
        block.setAttribute('data-id', `${this.sectionId}--block-${index + 1}`);
        block.append(card);
        track.append(block);
      });

      this._totals = { totalSlides: totalSlides, totalSlidesMobile: totalSlidesMobile };
      return shell;
    }

    buildNav() {
      if (!this.navTemplate || !this.headerNav || !this._totals) return null;
      if (this._totals.totalSlides === 1 && this._totals.totalSlidesMobile === 1) {
        return null;
      }

      const nav = this.navTemplate.content.firstElementChild.cloneNode(true);
      const total = nav.querySelector('.carousel-nav-arrow--total');
      total?.setAttribute('data-value', String(this._totals.totalSlides));
      total?.setAttribute('data-mobile-value', String(this._totals.totalSlidesMobile));
      return nav;
    }
  }

  customElements.define('recent-products-plus', RecentProductsPlus);
})();